import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/apiService';
import { toast } from 'react-toastify';

const ViewLeads = () => {
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [keyword, setKeyword] = useState('');
  const [status, setStatus] = useState('');
  const [dateRange, setDateRange] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [recordsPerPage] = useState(10);

  const fetchLeads = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        draw: 1,
        start: (currentPage - 1) * recordsPerPage,
        length: recordsPerPage,
        search: keyword,
        status: status,
        date: dateRange
      };
      
      const response = await apiService.post('leadList', params);
      if (response && response.data) {
        setLeads(response.data);
        setTotalRecords(response.recordsTotal || 0);
      }
    } catch (error) {
      toast.error('Failed to load leads');
      console.error('Leads fetch error:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, recordsPerPage, keyword, status, dateRange]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchLeads();
    }, keyword ? 500 : 0);
    
    return () => clearTimeout(timeoutId);
  }, [fetchLeads]);

  const handleStatusChange = async (id, newStatus) => {
    try {
      await apiService.get(`leadStatus/${id}`);
      setLeads(prevLeads => 
        prevLeads.map(lead => 
          lead._id === id ? { ...lead, status: newStatus } : lead
        )
      );
    } catch (error) {
      console.error('Status update error:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '--';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata'
    });
  };

  const totalPages = Math.ceil(totalRecords / recordsPerPage);

  return (
    <div className="p-0">
      <nav className="flex flex-wrap items-center text-xs sm:text-sm mb-6">
        <Link to="/dashboard" className="text-gray-500 hover:text-gray-700">Home</Link>
        <span className="mx-2 text-gray-400">/</span>
        <span className="text-gray-900 font-medium">Callbacks</span>
      </nav>

      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-4 sm:p-6 border-b">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <i className="bi bi-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                value={keyword}
                onChange={(e) => { setKeyword(e.target.value); setCurrentPage(1); }}
                placeholder="Type Name, Phone or Email"
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <input
              type="text"
              value={dateRange}
              onChange={(e) => { setDateRange(e.target.value); setCurrentPage(1); }}
              placeholder="Pick date range"
              className="w-full sm:w-full sm:w-[200px] px-2 sm:px-4 py-2 border border-gray-300 rounded-lg text-sm"
            />
            <select
              value={status}
              onChange={(e) => { setStatus(e.target.value); setCurrentPage(1); }}
              className="w-full sm:w-full sm:w-[150px] px-2 sm:px-4 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="">All</option>
              <option value="1">Open</option>
              <option value="0">Closed</option>
            </select>
          </div>
        </div>

        <div className="p-4 sm:p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#009ef7]"></div>
              <span className="ml-2 text-gray-600">Loading...</span>
            </div>
          ) : leads.length === 0 ? (
            <div className="text-center py-12">
              <i className="bi bi-chat-dots text-4xl text-gray-300 mb-2"></i>
              <h3 className="text-lg font-medium text-gray-900 mb-1">No Leads Found</h3>
              <p className="text-gray-500">No callback requests match your criteria.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs sm:text-sm">
                <thead className="bg-gray-50">
                  <tr className="text-left text-gray-500 text-xs uppercase">
                    <th className="px-2 py-3 w-10 text-center">#</th>
                    {user?.role === 'admin' && <th className="px-2 sm:px-4 py-3">Vendor Details</th>}
                    <th className="px-2 sm:px-4 py-3">CallBack for</th>
                    <th className="px-2 sm:px-4 py-3">Traveller Info</th>
                    <th className="px-2 sm:px-4 py-3">Trip / Stay Details</th>
                    <th className="px-2 sm:px-4 py-3">Added On / Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {leads.map((lead, index) => {
                    const vendorName = [lead.vendorId?.first_name, lead.vendorId?.last_name].filter(Boolean).join(' ') || lead.vendorId?.name || lead.vendorId?.username || '';
                    const vendorPhone = lead.vendorId?.phone || lead.vendorId?.phone_number || '';

                    const hasTripDetails = lead.travelDate || lead.travelerCount || lead.checkIn || lead.checkOut;

                    return (
                      <tr key={lead._id} className="hover:bg-gray-50">
                        <td className="px-2 py-3 text-center w-10 text-xs sm:text-sm text-gray-500 font-medium">{(currentPage - 1) * recordsPerPage + index + 1}</td>
                        {user?.role === 'admin' && (
                          <td className="px-2 sm:px-4 py-3">
                            {lead.vendorId ? (
                              <div className="flex flex-col gap-0.5 text-xs">
                                <Link to={`/view-profile/${lead.vendorId._id}`} className="font-semibold text-[#009ef7] hover:underline">
                                  {lead.vendorId.business_name || '--'}
                                </Link>
                                {vendorName && (
                                  <span className="text-gray-700 font-medium">
                                    {vendorName}
                                  </span>
                                )}
                                {vendorPhone && (
                                  <span className="text-gray-500 font-normal">
                                    📞 {vendorPhone}
                                  </span>
                                )}
                              </div>
                            ) : '--'}
                          </td>
                        )}
                        <td className="px-2 sm:px-4 py-3">
                          <div className="flex flex-col gap-1 items-start">
                            {lead.type && (
                              <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${lead.type?.toLowerCase() === 'hotel' ? 'bg-purple-100 text-purple-700' : 'bg-cyan-100 text-cyan-800'}`}>
                                {lead.type}
                              </span>
                            )}
                            {lead.type_id ? (
                              <a
                                href={`https://travellinglark.com/${lead.type === 'Package' ? 'package' : 'hotel'}/${lead.type_id.slug}/${lead.type_id.id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-block px-2 py-1 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded text-xs no-underline transition-colors font-medium"
                              >
                                {lead.type_id.name}
                              </a>
                            ) : '--'}
                          </div>
                        </td>
                        <td className="px-2 sm:px-4 py-3">
                          <div className="flex flex-col gap-0.5 text-xs">
                            {lead.name && (
                              <span className="font-semibold text-gray-900">{lead.name}</span>
                            )}
                            {lead.email && (
                              <span className="text-gray-600 font-normal">{lead.email}</span>
                            )}
                            {lead.phone && (
                              <span className="inline-block w-fit bg-green-100 text-black px-2 py-0.5 rounded-full text-[11px] font-medium mt-0.5">
                                {lead.phone}
                              </span>
                            )}
                            {!lead.name && !lead.email && !lead.phone && '--'}
                          </div>
                        </td>
                        <td className="px-2 sm:px-4 py-3">
                          {hasTripDetails ? (
                            <div className="flex flex-col gap-0.5 text-xs text-gray-700">
                              {lead.travelDate && (
                                <span><strong className="font-medium text-gray-900">Travel Date:</strong> {formatDate(lead.travelDate)}</span>
                              )}
                              {lead.travelerCount && (
                                <span><strong className="font-medium text-gray-900">Travelers:</strong> {lead.travelerCount}</span>
                              )}
                              {lead.checkIn && (
                                <span><strong className="font-medium text-gray-900">Check-In:</strong> {formatDate(lead.checkIn)}</span>
                              )}
                              {lead.checkOut && (
                                <span><strong className="font-medium text-gray-900">Check-Out:</strong> {formatDate(lead.checkOut)}</span>
                              )}
                            </div>
                          ) : '--'}
                        </td>
                        <td className="px-2 sm:px-4 py-3">
                          <div className="flex flex-col gap-1.5 text-xs">
                            <span className="text-gray-600 font-normal">{formatDateTime(lead.createdAt)}</span>
                            <select
                              value={lead.status}
                              onChange={(e) => handleStatusChange(lead._id, parseInt(e.target.value))}
                              className="text-xs border border-gray-300 rounded px-2 py-1 bg-white font-medium w-fit"
                            >
                              <option value="1">Open</option>
                              <option value="0">Closed</option>
                            </select>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {leads.length > 0 && (
          <div className="bg-white px-4 py-3 flex flex-col sm:flex-row items-center justify-between gap-3 border-t border-gray-200">
            <div>
              <p className="text-xs sm:text-sm text-gray-700">
                Showing <span className="font-medium">{(currentPage - 1) * recordsPerPage + 1}</span> to{' '}
                <span className="font-medium">{Math.min(currentPage * recordsPerPage, totalRecords)}</span> of{' '}
                <span className="font-medium">{totalRecords}</span> results
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                >
                  <i className="bi bi-chevron-left"></i>
                </button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let startPage = Math.max(1, currentPage - 2);
                  let endPage = Math.min(totalPages, startPage + 4);
                  if (endPage - startPage < 4) {
                    startPage = Math.max(1, endPage - 4);
                  }
                  const pageNum = startPage + i;
                  if (pageNum > totalPages) return null;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`relative inline-flex items-center px-2 sm:px-4 py-2 border text-sm font-medium ${
                        currentPage === pageNum
                          ? 'z-10 bg-[#009ef7]/10 border-[#009ef7] text-[#009ef7]'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                >
                  <i className="bi bi-chevron-right"></i>
                </button>
              </nav>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewLeads;
